import { useEffect, useRef, useState } from "react";
import { ActionIcon, Button, Group, Table, TextInput } from "@mantine/core";
import PropTypes from "prop-types";
import { IconTrash } from "@tabler/icons-react";
import { useAtom } from "jotai";
import { projectDetailsAtom } from "../../../utils/atoms/projectDetailsAtom";
import displayError from "../../../constants/utils";
import { mgaRegex } from "../../../constants/helper.js";
import { useTranslation } from "react-i18next";

const MAX_ROWS = 5;

const makeRow = (id) => ({
  id,
  expenditureName: "",
  capex: "",
  replacement: "",
  opex: "",
  lifetimeYear: "",
});

const normalizeRows = (capexItems) => {
  if (!Array.isArray(capexItems) || capexItems.length === 0) return [makeRow(1)];
  let idCounter = 1;
  return capexItems.map((row) => ({
    id: idCounter++,
    expenditureName: row?.expenditureName ?? "",
    capex: row?.capex ?? "",
    replacement: row?.replacement ?? "",
    opex: row?.opex ?? "",
    lifetimeYear: row?.lifetimeYear ?? "",
  }));
};

const toTrimmedString = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

const isValidExpenditureName = (value) => {
  const trimmed = toTrimmedString(value);
  if (trimmed === "") return true;
  if (trimmed.length < 1 || trimmed.length > 30) return false;
  return /^[a-zA-Z0-9 _-]*$/.test(trimmed);
};

const isNumericInput = (value) => /^\d*(\.\d*)?$/.test(value);
const isIntegerInput = (value) => /^-?\d*$/.test(value);

const stripRowsForSave = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      expenditureName: toTrimmedString(row?.expenditureName),
      capex: toTrimmedString(row?.capex),
      replacement: toTrimmedString(row?.replacement),
      opex: toTrimmedString(row?.opex),
      lifetimeYear: toTrimmedString(row?.lifetimeYear),
    }))
    .filter(
      (row) =>
        row.expenditureName !== "" ||
        row.capex !== "" ||
        row.replacement !== "" ||
        row.opex !== "" ||
        row.lifetimeYear !== ""
    );

const validateRow = (row, t) => {
  const tSafe = (key, opts = {}) => (t ? t(key, opts) : opts.defaultValue || key);
  const rawExpenditureName =
    row?.expenditureName === null || row?.expenditureName === undefined
      ? ""
      : String(row?.expenditureName);
  const expenditureName = toTrimmedString(row?.expenditureName);
  const capex = toTrimmedString(row?.capex);
  const replacement = toTrimmedString(row?.replacement);
  const opex = toTrimmedString(row?.opex);
  const lifetimeYear = toTrimmedString(row?.lifetimeYear);
  const anyCostEntered =
    capex !== "" || replacement !== "" || opex !== "";
  const fields = [expenditureName, capex, replacement, opex, lifetimeYear];
  const hasAnyField = fields.some((v) => v !== "");
  const allFieldsFilled = fields.every((v) => v !== "");

  let expenditureError = null;
  if (expenditureName === "" && (capex !== "" || replacement !== "" || opex !== "" || lifetimeYear !== "")) {
    expenditureError = tSafe("controller.validation.expenditureRequired");
  } else if (
    rawExpenditureName !== "" &&
    rawExpenditureName !== rawExpenditureName.trim()
  ) {
    expenditureError = tSafe("controller.validation.expenditureSpaces");
  } else if (/ {2,}/.test(expenditureName)) {
    expenditureError = tSafe("controller.validation.expenditureSingleSpace");
  } else if (expenditureName !== "") {
    if (expenditureName.length < 1 || expenditureName.length > 30) {
      expenditureError = tSafe("controller.validation.expenditureLength");
    } else if (!isValidExpenditureName(expenditureName)) {
      expenditureError = tSafe("controller.validation.expenditureAllowedChars");
    }
  }

  let costError = null;
  if (expenditureName !== "" && !anyCostEntered) {
    costError = tSafe("controller.validation.atLeastOneCost");
  } else if (capex !== "" && !mgaRegex?.zeroToTenPower9WithDecimalRegex.test(String(capex).trim())) {
    costError = tSafe("controller.validation.capexInvalid");
  }

  let replacementCostError = null;
  if (
    replacement !== "" &&
    !mgaRegex?.zeroToTenPower9WithDecimalRegex.test(String(replacement).trim())
  ) {
    replacementCostError = tSafe("controller.validation.replacementInvalid");
  }

  let omCostPerYearError = null;
  if (
    opex !== "" &&
    !mgaRegex?.zeroToTenPower9WithDecimalRegex.test(String(opex).trim())
  ) {
    omCostPerYearError = tSafe("controller.validation.omInvalid");
  }

  let lifetimeYearsError = null;
  if (lifetimeYear !== "" && !mgaRegex?.oneTo25Regex.test(String(lifetimeYear).trim())) {
    lifetimeYearsError = tSafe("controller.validation.lifetimeRange");
  }

  // Require all fields when any field is provided; mark only empty fields
  if (hasAnyField && !allFieldsFilled) {
    if (expenditureName === "") {
      expenditureError = tSafe("controller.validation.expenditureRequired");
    }
    if (capex === "") {
      costError = tSafe("controller.validation.capexRequired");
    }
    if (replacement === "") {
      replacementCostError = tSafe("controller.validation.replacementRequired");
    }
    if (opex === "") {
      omCostPerYearError = tSafe("controller.validation.omRequired");
    }
    if (lifetimeYear === "") {
      lifetimeYearsError = tSafe("controller.validation.lifetimeRequired");
    }
  }

  return {
    expenditureError,
    costError,
    replacementCostError,
    omCostPerYearError,
    lifetimeYearsError,
  };
};

const findDuplicateExpenditures = (rows) => {
  const counts = {};
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const key = toTrimmedString(row?.expenditureName).toLowerCase();
    if (key === "") return;
    counts[key] = (counts[key] || 0) + 1;
  });
  return new Set(
    Object.entries(counts)
      .filter(([, count]) => count > 1)
      .map(([expenditureName]) => expenditureName)
  );
};

const computeErrorsById = (rows, t) => {
  const duplicates = findDuplicateExpenditures(rows);
  const next = {};
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const baseErrors = validateRow(row, t);
    const key = toTrimmedString(row?.expenditureName).toLowerCase();
    const isDuplicate = key !== "" && duplicates.has(key);
    next[row.id] = {
      ...baseErrors,
      expenditureError: isDuplicate
        ? t("controller.validation.expenditureUnique")
        : baseErrors.expenditureError,
    };
  });
  return next;
};

const CapexTable = ({ capexItems, onSave, onValidationChange, showErrors = false, getFieldAttributes }) => {
  const { t } = useTranslation();
  const [projectDetails] = useAtom(projectDetailsAtom);
  const [rows, setRows] = useState(() => normalizeRows(capexItems));
  const [errorsById, setErrorsById] = useState(() =>
    computeErrorsById(normalizeRows(capexItems), t)
  );
  const lastAppliedValueRef = useRef("");
  const lastEmittedValueRef = useRef("");
  const onSaveRef = useRef(onSave);

  useEffect(() => {
    if (!onValidationChange) return;
    const hasAnyError = Object.values(errorsById || {}).some(
      (e) =>
        e?.expenditureError ||
        e?.costError ||
        e?.replacementCostError ||
        e?.omCostPerYearError ||
        e?.lifetimeYearsError
    );
    onValidationChange(hasAnyError);
  }, [errorsById, onValidationChange]);

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    const incomingSerialized = JSON.stringify(stripRowsForSave(capexItems));
    if (incomingSerialized === lastAppliedValueRef.current) return;

    lastAppliedValueRef.current = incomingSerialized;

    const normalized = normalizeRows(capexItems);

    setRows((prev) => {
      const prevSerialized = JSON.stringify(stripRowsForSave(prev));
      if (prevSerialized === incomingSerialized) return prev;
      return normalized;
    });

    setErrorsById(computeErrorsById(normalized, t));
  }, [capexItems, t]);

  const emitIfValid = (nextRows, nextErrorsById) => {
    if (!onSaveRef.current) return;
    const hasAnyError = Object.values(nextErrorsById || {}).some(
      (e) =>
        e?.expenditureError ||
        e?.costError ||
        e?.replacementCostError ||
        e?.omCostPerYearError ||
        e?.lifetimeYearsError
    );
    if (hasAnyError) return;
    const cleaned = stripRowsForSave(nextRows);
    const serialized = JSON.stringify(cleaned);
    if (serialized === lastEmittedValueRef.current) return;
    lastEmittedValueRef.current = serialized;
    onSaveRef.current(cleaned);
  };

  const updateRow = (id, patch) => {
    setRows((prev) => {
      const index = prev.findIndex((r) => r.id === id);
      if (index === -1) return prev;
      const nextRow = { ...prev[index], ...patch };
      const nextRows = [...prev];
      nextRows[index] = nextRow;
      const nextErrors = computeErrorsById(nextRows, t);
      setErrorsById(nextErrors);
      emitIfValid(nextRows, nextErrors);
      return nextRows;
    });
  };

  const deleteRow = (id) => {
    setRows((prev) => {
      const remaining = prev.filter((r) => r.id !== id);
      const nextRows = remaining.length === 0 ? [makeRow(1)] : remaining;

      const nextErrors = computeErrorsById(nextRows, t);
      setErrorsById(nextErrors);
      emitIfValid(nextRows, nextErrors);


      return nextRows;
    });
  };

  const addRow = () => {
    setRows((prev) => {
      if ((prev?.length || 0) >= MAX_ROWS) {
        displayError(
          "error",
          t("controller.validation.maxRows", { count: MAX_ROWS })
        );
        return prev;
      }
      const nextId = Math.max(0, ...prev.map((r) => r.id || 0)) + 1;
      const newRow = makeRow(nextId);
      const nextRows = [...prev, newRow];

      const nextErrors = computeErrorsById(nextRows, t);
      setErrorsById(nextErrors);
      emitIfValid(nextRows, nextErrors);

      return nextRows;
    });
  };

  const lastEditableRow = rows?.[Math.max(0, (rows?.length || 1) - 1)];
  const hasAllFields = [
    "expenditureName",
    "capex",
    "replacement",
    "opex",
    "lifetimeYear",
  ].every((field) => toTrimmedString(lastEditableRow?.[field]) !== "");
  const canAddRow =
    !!lastEditableRow &&
    hasAllFields &&
    (rows?.length || 0) < MAX_ROWS;

  // const totals = rows?.reduce(
  //   (acc, row) => {
  //     const toNumber = (value) => {
  //       const num = parseFloat(value);
  //       return Number.isFinite(num) ? num : 0;
  //     };
  //     acc.capex += toNumber(row.capex);
  //     acc.replacement += toNumber(row.replacement);
  //     acc.opex += toNumber(row.opex);
  //     return acc;
  //   },
  //   { capex: 0, replacement: 0, opex: 0 }
  // );

  return (
    <>
      <Group justify="center" mt="md" className="mb-3">
        <Button aria-label="capex-add-item" variant="outline" onClick={addRow} disabled={!canAddRow}>
          {t("controller.capexTable.addItem")}
        </Button>
      </Group>
      <Table withBorder withColumnBorders highlightOnHover>
        <thead>
          <tr>
            <th className="text-align-center">{t("controller.capexTable.itemDescription")}</th>
            <th className="text-align-center">{t("controller.capexTable.capitalExpenditure")} ({projectDetails?.projectDTO?.currencyCode || "$"})</th>
                        <th className="text-align-center">{t("controller.capexTable.operationAndMaintenanceCost")} ({projectDetails?.projectDTO?.currencyCode || "$"}/{t("common.years")})</th>

            <th className="text-align-center">{t("controller.capexTable.replacementCost")} ({projectDetails?.projectDTO?.currencyCode || "$"})</th>
            <th className="text-align-center">{t("controller.capexTable.lifetime")} ({t("controller.common.perYearDollars")})</th>
            <th className="text-align-center" style={{ width: 110 }}>
              {t("controller.capexTable.actions")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const disableDelete =
              rows.length === 1 &&
              toTrimmedString(row.expenditureName) === "" &&
              toTrimmedString(row.capex) === "" &&
              toTrimmedString(row.replacement) === "" &&
              toTrimmedString(row.opex) === "" &&
              toTrimmedString(row.lifetimeYear) === "";
            const rowErrors = errorsById[row.id] || {};
            return (
              <tr key={row.id}>
                <td>
                  <TextInput
                  {...getFieldAttributes("field-8")}
                   data-id="field-8"
                    value={row.expenditureName}
                    onChange={(e) =>
                      updateRow(row.id, { expenditureName: e.target.value })
                    }
                    onKeyDown={(e) => {
                      if (e.key === " ") {
                        e.stopPropagation();
                      }
                    }}
                    error={showErrors ? rowErrors.expenditureError : null}
                  />
                </td>
                <td>
                  <TextInput
                   {...getFieldAttributes("field-1")}
                    data-id="field-1"
                    value={row.capex}
                    onChange={(e) => {
                      const next = e.target.value;
                      if (next === "" || isNumericInput(next)) {
                        updateRow(row.id, { capex: next });
                      }
                    }}
                    inputMode="decimal"
                    error={showErrors ? rowErrors.costError : null}
                  />
                </td>
                                <td>
                  <TextInput
                   {...getFieldAttributes("field-3")}
                    data-id="field-3"
                    value={row.opex}
                    onChange={(e) => {
                      const next = e.target.value;
                      if (next === "" || isNumericInput(next)) {
                        updateRow(row.id, { opex: next });
                      }
                    }}
                    inputMode="decimal"
                    error={showErrors ? rowErrors.omCostPerYearError : null}
                  />
                </td>
                <td>
                  <TextInput
                  {...getFieldAttributes("field-2")}
                    data-id="field-2"
                    value={row.replacement}
                    onChange={(e) => {
                      const next = e.target.value;
                      if (next === "" || isNumericInput(next)) {
                        updateRow(row.id, { replacement: next });
                      }
                    }}
                    inputMode="decimal"
                    error={showErrors ? rowErrors.replacementCostError : null}
                  />
                </td>

                <td>
                  <TextInput
                   {...getFieldAttributes("field-4")}
                    data-id="field-4"
                    value={row.lifetimeYear}
                    onChange={(e) => {
                      const next = e.target.value;
                      if (next === "" || isIntegerInput(next)) {
                        updateRow(row.id, { lifetimeYear: next });
                      }
                    }}
                    inputMode="decimal"
                    error={showErrors ? rowErrors.lifetimeYearsError : null}
                  />
                </td>
                <td className="d-flex justify-content-center">
                  <ActionIcon
                    variant="subtle"
                    aria-label={`capex-delete-row-${row.id}`}
                    title={t("controller.capexTable.delete")}
                    onClick={() => deleteRow(row.id)}
                    disabled={disableDelete}
                    className="bg-transparent border-0"
                  >
                    <IconTrash size={18} />
                  </ActionIcon>
                </td>
              </tr>
            );
          })}
          {/* <tr>
            <td  className="fw-bold text-end ">
              Total
            </td>
            <td>
              <TextInput
                value={String(totals.capex)}
                readOnly
              />
            </td>
            <td>
              <TextInput
                value={String(totals.opex)}
                readOnly
              />
            </td>
             <td>
              <TextInput
                value={String(totals.replacement)}
                readOnly
              />
            </td>
            <td>
              <TextInput
                value=""
                readOnly
                aria-hidden="true"
                styles={{ input: { visibility: "hidden", pointerEvents: "none" } }}
              />
            </td>
            <td className="d-flex justify-content-center">
              <TextInput
                value=""
                readOnly
                aria-hidden="true"
                styles={{ input: { visibility: "hidden", pointerEvents: "none" } }}
              />
            </td>
          </tr> */}
        </tbody>
      </Table>

    </>
  );
};

CapexTable.propTypes = {
  capexItems: PropTypes.arrayOf(
    PropTypes.shape({
      expenditureName: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      capex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      replacement: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      opex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      lifetimeYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  onSave: PropTypes.func,
  onValidationChange: PropTypes.func,
  showErrors: PropTypes.bool,
};

export default CapexTable;
